import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import MaintenanceTimeline from '../components/MaintenanceTimeline'
import CostSummaryCard from '../components/CostSummaryCard'
import ServiceComplianceBadge from '../components/ServiceComplianceBadge'
import SharedPageFooter from '../components/SharedPageFooter'

function formatDate(dateStr) {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${parseInt(month)}/${parseInt(day)}/${year}`
}

export default function SharedVehiclePage() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    axios.get(`/api/share/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [token])

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`/api/share/${token}/pdf`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `${data.vehicle.display_name.replace(/\s+/g, '-').toLowerCase()}-maintenance-log.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('Failed to download PDF')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p className="text-muted">Loading...</p>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center">
        <h1 className="text-3xl font-extrabold text-ink mb-2">Not found</h1>
        <p className="text-muted mb-6">This maintenance log is no longer available.</p>
        <Link to="/" className="text-accent hover:text-accent-dark text-sm font-medium">
          Go to MyWrenchLog
        </Link>
      </div>
    )
  }

  const { vehicle, maintenance_logs, premium, cost_summary, service_compliance } = data
  const unit = vehicle.meter_label || 'mi'

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <header className="border-b-[3px] border-ink">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="" className="h-8 w-8" />
            <span className="text-base sm:text-lg font-extrabold tracking-tight text-ink">MyWrenchLog</span>
          </Link>
          <span className="text-[11px] font-bold uppercase tracking-widest text-muted">Shared log</span>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center gap-4 mb-6">
          {vehicle.photo_url ? (
            <img
              src={vehicle.photo_url}
              alt={vehicle.display_name}
              className="w-[72px] h-[72px] object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-[72px] h-[72px] bg-ink text-paper flex items-center justify-center font-extrabold flex-shrink-0 text-2xl tracking-wide">
              {(vehicle.model || '').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-4xl font-extrabold text-ink leading-tight">{vehicle.display_name}</h1>
            {vehicle.vin && (
              <p className="text-xs sm:text-sm text-muted font-mono mt-1 break-all">{vehicle.vin}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-line border-y-[1.5px] border-ink mb-6">
          {[
            [unit === 'hrs' ? 'Current hours' : 'Odometer', vehicle.current_meter ? `${vehicle.current_meter.toLocaleString()} ${unit}` : `- ${unit}`],
            [unit === 'hrs' ? 'Hours logged' : 'Driven since purchase', vehicle.purchase_meter != null && vehicle.current_meter != null ? `${(vehicle.current_meter - vehicle.purchase_meter).toLocaleString()} ${unit}` : '-'],
            ['Service records', vehicle.maintenance_count],
            vehicle.engine_model && ['Engine', vehicle.engine_model],
            vehicle.body_class && ['Body', vehicle.body_class],
            vehicle.purchase_date && ['Purchased', formatDate(vehicle.purchase_date)],
          ].filter(Boolean).map(([label, value]) => (
            <div key={label} className="bg-paper px-4 py-3 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted">{label}</p>
              <p className="mt-1 font-mono text-base sm:text-lg font-medium text-ink break-words">{value}</p>
            </div>
          ))}
        </div>

        {/* Premium: compliance + cost summary */}
        {premium && <ServiceComplianceBadge serviceCompliance={service_compliance} />}
        {premium && <CostSummaryCard costSummary={cost_summary} meterLabel={unit} />}

        {/* Maintenance History */}
        <div className="flex items-center justify-between gap-3 pb-3 border-b-[3px] border-ink">
          <h2 className="text-xl sm:text-2xl font-extrabold text-ink">Maintenance history</h2>
          {premium && (
            <button
              onClick={handleDownloadPdf}
              className="px-3 py-2 text-sm font-bold text-ink border-[1.5px] border-ink hover:bg-white whitespace-nowrap"
            >
              Download PDF
            </button>
          )}
        </div>

        <MaintenanceTimeline
          visits={maintenance_logs}
          meterLabel={unit}
        />

        {/* Free tier teaser */}
        {!premium && maintenance_logs.length > 0 && (
          <div className="mt-6 border border-dashed border-line p-4 text-center">
            <p className="text-sm text-muted">
              Photos, receipts, and cost analytics are available when the owner upgrades to Premium.
            </p>
          </div>
        )}
      </main>

      <SharedPageFooter />
    </div>
  )
}
